import React, { useRef, useState, useEffect } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { MenuItem as MuiMenuItem, Tooltip, Fade } from '@mui/material';
import { withStyles } from '@mui/styles';
import { MAIN_THEME_COLOR } from '../../../const';
import { isMobile } from '../../services/isMobile';

export const menuItemStyles = {
  minHeight: '40px !important',
  fontSize: '14px',
  fontWeight: 400,
  padding: '0 10px',
  transition: 'all .15s ease',
  maxWidth: 550,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  display: 'flex',
  alignItems: 'center',

  '&.flat-menu .menu-item__root': {
    display: 'flex',
    alignItems: 'center',
  },

  '& .menu-item__root': {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '100%',
  },

  '& .MuiIconButton-label svg': {
    marginRight: 10,
  },

  '&.Mui-selected': {
    color: MAIN_THEME_COLOR,
    fontWeight: 600,

    '&:hover': {
      color: MAIN_THEME_COLOR,
      fontWeight: 600,
    },
  },

  '&:hover': {
    color: MAIN_THEME_COLOR,
    fontWeight: 600,
    fontSize: 16,

    '& .MuiCheckbox-root': {
      color: MAIN_THEME_COLOR,
    },

    '& .MuiTypography-root': {
      fontWeight: 600,
    },
  },
};

const CustomizedMenuItem = withStyles({ root: menuItemStyles })(MuiMenuItem);

export const MenuItem = React.forwardRef(({ children, title, isFlat, fullWidth, style = {}, ...otherProps }, ref) => {
  const textElementRef = useRef();
  const [hoverStatus, setHover] = useState(false);
  const [opened, setOpened] = useState(false);

  const compareSize = () => {
    if (textElementRef.current) {
      const compare = textElementRef.current.scrollWidth > textElementRef.current.clientWidth;

      setHover(compare);
    }
  };

  useEffect(() => {
    compareSize();
    window.addEventListener('resize', compareSize);

    return () => {
      window.removeEventListener('resize', compareSize);
    };
  }, []);

  const handleOpen = () => {
    setOpened(true);
    compareSize();
  };

  const handleClose = () => {
    setOpened(false);
  };

  const handleMouseEnter = () => {
    handleOpen();
    setTimeout(() => {
      compareSize();
    }, 150);
  };

  const isOpenedTooltip = title && opened && hoverStatus;
  const isDisableTooltipHandlers = isMobile || !title || !hoverStatus;

  return (
    <CustomizedMenuItem
      ref={ref}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleClose}
      className={clsx({ 'flat-menu': isFlat })}
      style={{
        ...style,
        ...(fullWidth
          ? {
              maxWidth: 'none',
            }
          : {}),
      }}
      {...otherProps}
    >
      <Tooltip
        title={title}
        disableHoverListener={isDisableTooltipHandlers}
        TransitionComponent={Fade}
        TransitionProps={{ timeout: 150 }}
        open={isOpenedTooltip}
        placement="right"
        PopperProps={{
          style: {
            boxShadow: 'none',
          },
        }}
      >
        <div className="menu-item__root" ref={textElementRef}>
          {children}
        </div>
      </Tooltip>
    </CustomizedMenuItem>
  );
});

MenuItem.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
  value: PropTypes.any,
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
};

MenuItem.defaultProps = {
  disabled: false,
};

export default MenuItem;
